import type { ServerError, ServerParseError } from '@apollo/client';
import type { ErrorHandler } from '@apollo/client/link/error';
import { navigate } from '@gatsbyjs/reach-router';
import { cond, equals, T } from 'ramda';
import { ErrorTypes } from '~/Errors';
import { getErrorType } from '~/util/errorHandling';

/** Log out users with full page reload */
const handleNetworkError = (
  error: Error | ServerError | ServerParseError,
): void => {
  if (error.message === 'No current user') {
    window.location.replace('/logout');
    return;
  }

  // 401 Unauthorized
  if ('statusCode' in error && error.statusCode === 401) {
    window.location.replace('/logout');
    return;
  }

  if ('response' in error) {
    const response = (error as ServerError).response;
    if (response && 'status' in response && response.status === 401) {
      window.location.replace('/logout');
    }
  }
};

const handleGraphQLError = (errorType: string | null): void =>
  cond([
    [equals(ErrorTypes.accountLockedError), () => void navigate('/-/locked')],
    [equals(ErrorTypes.accountPausedError), () => void navigate('/-/paused')],
    [
      equals(ErrorTypes.accountCancelledError),
      () => void navigate('/-/cancelled'),
    ],
    [
      equals(ErrorTypes.refreshRequiredError),
      () => void window.location.reload(),
    ],
    [T, () => {}],
  ])(errorType);

const errorHandler: ErrorHandler = ({ graphQLErrors, networkError }) => {
  if (networkError) {
    handleNetworkError(networkError);
    return;
  }

  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    handleGraphQLError(getErrorType(graphQLErrors[0]));
  }
};

export default errorHandler;
